/**
 * The recording agreement, before anything else in a multiplayer room.
 *
 * First, because it is the only gate a player can say no to on principle: the
 * microphone check would otherwise be asking for a microphone we then ask
 * permission to record. Nothing is recorded and no seat is taken until the box
 * is ticked and the button pressed — the join is sent after the gates, and the
 * server refuses one without this agreement anyway.
 *
 * Two faces on one card: the agreement, and what a player who said no sees.
 * The second is not a dead end — they can go back and agree, or leave.
 */

import { useState } from 'react'

interface ConsentGateProps {
  step: number
  of: number
  /** A refusal from the server, shown above the agreement when there is one. */
  error?: string | null
  onAgree(): void
  onDecline(): void
}

export function ConsentGate({ step, of, error, onAgree, onDecline }: ConsentGateProps) {
  const [ticked, setTicked] = useState(false)

  return (
    <section className="gate gate-consent" aria-labelledby="consent-title">
      <p className="gate-step">
        Step {step} of {of}
      </p>
      <h1 id="consent-title">Before you play</h1>
      {error && <p className="error">{error}</p>}
      <p>We record everything your microphone picks up, and everything you do in the kitchen, during each round.</p>
      <label className="consent-check">
        <input
          type="checkbox"
          checked={ticked}
          onChange={(event) => setTicked(event.target.checked)}
          data-testid="consent-tick"
        />
        I agree to be recorded.
      </label>
      <div className="gate-actions">
        <button type="button" className="consent-agree" disabled={!ticked} onClick={onAgree} data-testid="consent-agree">
          Agree and continue
        </button>
        <button type="button" onClick={onDecline} data-testid="consent-decline">
          No thanks
        </button>
      </div>
    </section>
  )
}

interface ConsentDeclinedProps {
  onReview(): void
  onLeave(): void
}

export function ConsentDeclined({ onReview, onLeave }: ConsentDeclinedProps) {
  return (
    <section className="gate gate-consent" aria-labelledby="consent-declined-title">
      <h1 id="consent-declined-title">You can't join this game</h1>
      <p className="hint">You need to agree to recording to play. Nothing was recorded.</p>
      <div className="gate-actions">
        <button type="button" onClick={onReview} data-testid="consent-review">
          Go back
        </button>
        <button type="button" onClick={onLeave} data-testid="consent-home">
          Back to home
        </button>
      </div>
    </section>
  )
}
